import ReviewStatus from "../../config/ReviewStatus";
import { QUICK_ACTION_LABELS, QUICK_ACTIONS_FIELD } from "../../utils/QuickActions";

// Priority ranking for the Quick Actions column (lower = more actionable, sorts first).
const QUICK_ACTION_PRIORITY = {
  [QUICK_ACTION_LABELS.assigned]: 1,
  [QUICK_ACTION_LABELS.readyForConsensus]: 2,
  [QUICK_ACTION_LABELS.consensusInProgress]: 3,
  [QUICK_ACTION_LABELS.consensusComplete]: 4,
  [QUICK_ACTION_LABELS.newReview]: 5,
};

function descendingComparator(a, b, orderBy) {
  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((nestedObj, key) => nestedObj && nestedObj[key], obj);
  };

  let aValue, bValue;

  // Special case for sorting the program summary variants table Latest Review Status column id
  if (orderBy === 'interpretationStatusString') {
    aValue = getReviewStatusText(a) || '';
    bValue = getReviewStatusText(b) || '';
  } else {
    aValue = getNestedValue(a, orderBy);
    bValue = getNestedValue(b, orderBy);
  }

  // Special case for sorting the Quick Actions column by action priority; rows with no actions sort last.
  // The Program Summary variants table uses the column id 'status'; other tables use the 'quickActions' id.
  // Guard the 'status' alias to rows carrying the synthetic Quick Actions field so tables that also use
  // id 'status' (Workflow Runs, Pipeline Steps) keep their normal value-based sort.
  if (orderBy === QUICK_ACTIONS_FIELD || (orderBy === 'status' && (Array.isArray(a[QUICK_ACTIONS_FIELD]) || Array.isArray(b[QUICK_ACTIONS_FIELD])))) {
    const rank = (row) => {
      const actions = row[QUICK_ACTIONS_FIELD] || [];
      if (actions.length === 0) return 99;
      return Math.min(...actions.map((action) => QUICK_ACTION_PRIORITY[action] ?? 98));
    };
    return rank(a) - rank(b);
  }

  // Special case for sorting the Program Dashboard variants table Action column by the button it shows.
  // Mirrors the actionButton logic there: Delete (open review) first, then Create, then Reopen, none last.
  if (orderBy === 'reviewAction') {
    const rank = (row) => {
      if (row.currentReviewVersion == 1 && row.reviewStatus !== ReviewStatus.CLOSED) return 99; // no button shown
      switch (row.reviewStatus) {
        case ReviewStatus.CLOSED: return 2;  // Create new review
        case ReviewStatus.DELETED: return 3; // Reopen review
        default: return 1;                   // Delete review
      }
    };
    return rank(a) - rank(b);
  }

  // Special case for sorting program summary variants table Patients column id
  if (orderBy === 'numPatientsIdentified') {
    const aNumPatients = a.patientsList ? a.patientsList.length : 0;
    const bNumPatients = b.patientsList ? b.patientsList.length : 0;
    return bNumPatients - aNumPatients;
  }

  // Special case for sorting Flags column (Test Results table) by severity
  if (orderBy === 'reason') {
    const getSeverityOrder = (row) => {
      if (!row.valueStatus || row.valueStatus !== 'Abnormal') return 4; // No flag (empty)
      const status = (row.flaggedStatus || '').toLowerCase();
      if (status === 'strong') return 1; // High
      if (status === 'moderate') return 2; // Medium
      if (status === 'mild') return 3; // Low
      return 4; // Unknown/empty
    };
    const aOrder = getSeverityOrder(a);
    const bOrder = getSeverityOrder(b);
    if (aOrder !== bOrder) return bOrder - aOrder;
    // If same severity, sort alphabetically by code
    return (b.code || '').localeCompare(a.code || '');
  }

  // Handle date sorting
  if (orderBy.toLowerCase().indexOf('date') > -1 || orderBy === 'closedDate') {
    const aDate = new Date(aValue).valueOf();
    const bDate = new Date(bValue).valueOf();

    if (!isNaN(bDate) && !isNaN(aDate)) {
      return bDate < aDate ? -1 : bDate > aDate ? 1 : 0;
    } else {
      if (isNaN(bDate)) return -1;
      if (isNaN(aDate)) return 1;
    }
  }

  // Fix for numeric columns: empty values first, then 0, then positive numbers
  if (typeof aValue === 'number' || typeof bValue === 'number') {
    const aIsEmpty = aValue === null || aValue === undefined || aValue === '';
    const bIsEmpty = bValue === null || bValue === undefined || bValue === '';
    if (aIsEmpty && bIsEmpty) return 0;
    if (aIsEmpty) return 1; // a is empty, b is not: b comes first
    if (bIsEmpty) return -1; // b is empty, a is not: a comes first
    // Both are numbers, sort numerically
    return bValue - aValue;
  }

  // Default alphabetical sorting for strings and fallback for other types
  if (typeof aValue === 'string' || typeof bValue === 'string') {
    aValue = aValue || '';
    bValue = bValue || '';
    return bValue.localeCompare(aValue);
  }

  // Fallback for other types
  if (bValue < aValue) return -1;
  if (bValue > aValue) return 1;
  return 0;
}

const getReviewStatusText = (row) => {
  switch (row.reviewStatus) {
    case ReviewStatus.CLOSED:
      return 'Consensus review complete';
    case ReviewStatus.READY_FOR_CONSENSUS:
      return 'Ready for consensus review';
    case ReviewStatus.CONSENSUS_IN_PROGRESS:
      return 'Consensus review in progress';
    default:
      return row.interpretationStatusString || '';
  }
};

export function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

export function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

export function handleRequestSort(event, property, order, orderBy, setOrder, setOrderBy) {
  const isAsc = orderBy === property && order === 'asc';
  setOrder(isAsc ? 'desc' : 'asc');
  setOrderBy(property);
}

export function handleChangePage(event, newPage, setPage) {
  setPage(newPage);
}

export function handleChangeRowsPerPage(event, setRowsPerPage, setPage) {
  setRowsPerPage(parseInt(event.target.value, 10));
  setPage(0);
}
